// The Scan PDF form only offers subjects and chapters the institute really has.
//
// The pattern / class / subject / chapter lists come from the Aulas AI dropdown service, which
// knows a standard JEE/NEET syllabus. An institute only has the subjects and chapters it has
// set up, and a test can only be created against those. So the screen reads the institute's own
// lists (the same calls the Create Test form makes) and hides everything it does not have.

import { Api } from 'services';

// ---- name matching -----------------------------------------------------------

export const normaliseName = (name) =>
  String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Same rule the test import uses to file a question under a chapter: drop a leading
// "Chapter 5 -" style number, then compare on lowercase letters and digits.
export const normaliseChapterName = (name) =>
  String(name ?? '')
    .toLowerCase()
    .replace(/^(chapter|chap|ch|unit|lesson)[\s.\-:)]*\d+[\s.\-:)]*/, '')
    .replace(/[^a-z0-9]/g, '');

// Institute subjects the given generator subject would be saved under: an exact name, or an
// institute name that contains it ("Physics" inside "Physics - JEE"). This is the same
// substring search the hand-off performs, so whatever is offered here will resolve there.
export const matchingSubjects = (aiSubjectName, instituteSubjects) => {
  const key = normaliseName(aiSubjectName);
  if (!key) return [];
  return instituteSubjects.filter((s) => {
    const other = normaliseName(s.name);
    return other === key || (key.length >= 3 && other.includes(key));
  });
};

// Same loose rule as the test import: equal after normalising, or one contains the other.
// Very short names are not allowed to match by containment, so "Heat" cannot claim every
// chapter that happens to contain "he".
export const chapterAvailable = (aiChapterName, instituteChapterKeys) => {
  const key = normaliseChapterName(aiChapterName);
  if (!key) return false;
  if (instituteChapterKeys.has(key)) return true;
  for (const other of instituteChapterKeys) {
    if (Math.min(other.length, key.length) >= 4 && (other.includes(key) || key.includes(other))) return true;
  }
  return false;
};

// ---- institute calls (same endpoints as the Create Test form) -----------------

const get = (url, params) =>
  new Promise((resolve) => {
    let request = Api.get(url);
    if (request.suppressSnackbar) request = request.suppressSnackbar();
    if (params) request = request.params(params);
    request.send((response) => resolve(response));
  });

const asList = (response) =>
  Array.isArray(response) ? response : Array.isArray(response && response.data) ? response.data : null;

// { ok: true, subjects: [{ id, name }] }, or { ok: false } when the institute could not be read,
// in which case the screen shows the full list rather than an empty one.
export const loadInstituteSubjects = async () => {
  const list = asList(await get('/testv2/subject/autocomplete?name=&standardId='));
  if (!list) return { ok: false, subjects: [] };
  const seen = new Set();
  const subjects = [];
  list.forEach((item) => {
    const name = String(item?.value ?? item?.name ?? '').trim();
    if (!name || item?.id === undefined || item?.id === null || seen.has(String(item.id))) return;
    seen.add(String(item.id));
    subjects.push({ id: String(item.id), name });
  });
  return { ok: true, subjects };
};

// Chapters exist per batch, so read the batches that teach these subjects, then each batch's
// chapters. Returns { ok, keys: Set of normalised chapter names }.
export const loadInstituteChapters = async (subjectIds) => {
  const ids = (subjectIds || []).map(String);
  if (ids.length === 0) return { ok: true, keys: new Set() };
  const standards = asList(await get('/testv2/standard/autocomplete', { subjectIdList: ids, chapterIdList: [] }));
  if (!standards) return { ok: false, keys: new Set() };
  const perStandard = await Promise.all(
    standards.map((std) => get('/testv2/chapter/autocomplete', { standardId: String(std.id), subjectIds: ids, name: '' }))
  );
  const keys = new Set();
  let failed = false;
  perStandard.forEach((response) => {
    const list = asList(response);
    if (!list) { failed = true; return; }
    list.forEach((chapter) => {
      const key = normaliseChapterName(chapter?.name);
      if (key) keys.add(key);
    });
  });
  // If some batches could not be read, an incomplete list would hide chapters that do exist.
  return { ok: !failed, keys };
};
